import { RegisterOptions } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EditAccountFormValues, NewAccountFormValues } from '../models'
import dayjs, { ConfigType } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { FORMAT_DATE } from '../../../../shared/constants/app.constants'
dayjs.extend(utc)
dayjs.extend(timezone)

export const useFormRules = (): Record<
  keyof NewAccountFormValues,
  Omit<RegisterOptions<NewAccountFormValues>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled' | 'value'>
> => {
  const { t } = useTranslation()
  return {
    clientName: {
      maxLength: {
        value: 200,
        message: t('AccountSetup:new_account_form.validation.clientNameMaxLength', { maxLength: 200 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.clientNameRequired'),
      },
    },
    accountName: {
      maxLength: {
        value: 200,
        message: t('AccountSetup:new_account_form.validation.accountNameMaxLength', { maxLength: 200 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.accountNameRequired'),
      },
    },
    accountNumber: {
      maxLength: {
        value: 10,
        message: t('AccountSetup:new_account_form.validation.accountNumberMaxLength', { maxLength: 10 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.accountNumberRequired'),
      },
    },
    chargeAccountNumber: {
      maxLength: {
        value: 255,
        message: t('AccountSetup:new_account_form.validation.chargeAccountNumberMaxLength', { maxLength: 255 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.chargeAccountNumberRequired'),
      },
    },
    displayNotes: {
      maxLength: {
        value: 255,
        message: t('AccountSetup:new_account_form.validation.accountNumberMaxLength', { maxLength: 255 }),
      },
    },
    serviceTypeParameter: {
      maxLength: {
        value: 13,
        message: t('AccountSetup:new_account_form.validation.serviceTypeParameterMaxLength', { maxLength: 13 }),
      },
    },
    checkExceptionProcessingType: {},
    checkDecision: {},
    checkReason: {},
    staleDatedDays: {
      maxLength: {
        value: 3,
        message: t('AccountSetup:new_account_form.validation.staleDatedDaysMaxLength', { maxLength: 3 }),
      },
      min: {
        value: 60,
        message: t('AccountSetup:new_account_form.validation.staleDatedDaysRange', { min: 60, max: 365 }),
      },
      max: {
        value: 365,
        message: t('AccountSetup:new_account_form.validation.staleDatedDaysRange', { min: 60, max: 365 }),
      },
    },
    payeeMatch: {
      maxLength: 3,
    },
    achExceptionProcessingType: {},
    achDecision: {},
    achReason: {},
  }
}

export const useEditFormRules = (): Record<
  keyof EditAccountFormValues,
  Omit<RegisterOptions<EditAccountFormValues>, 'value' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>
> => {
  const { t } = useTranslation()
  return {
    clientName: {
      maxLength: {
        value: 200,
        message: t('AccountSetup:new_account_form.validation.clientNameMaxLength', { maxLength: 200 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.clientNameRequired'),
      },
    },
    accountName: {
      maxLength: {
        value: 200,
        message: t('AccountSetup:new_account_form.validation.accountNameMaxLength', { maxLength: 200 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.accountNameRequired'),
      },
    },
    accountNumber: {
      maxLength: {
        value: 10,
        message: t('AccountSetup:new_account_form.validation.accountNumberMaxLength', { maxLength: 10 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.accountNumberRequired'),
      },
    },
    chargeAccountNumber: {
      maxLength: {
        value: 255,
        message: t('AccountSetup:new_account_form.validation.chargeAccountNumberMaxLength', { maxLength: 255 }),
      },
      required: {
        value: true,
        message: t('AccountSetup:new_account_form.validation.chargeAccountNumberRequired'),
      },
    },
    displayNotes: {
      maxLength: {
        value: 255,
        message: t('AccountSetup:new_account_form.validation.accountNumberMaxLength', { maxLength: 255 }),
      },
    },
    serviceTypeParameter: {
      maxLength: {
        value: 13,
        message: t('AccountSetup:new_account_form.validation.serviceTypeParameterMaxLength', { maxLength: 13 }),
      },
    },
    checkExceptionProcessingType: {},
    checkDecision: {},
    checkReason: {},
    staleDatedDays: {
      maxLength: {
        value: 3,
        message: t('AccountSetup:new_account_form.validation.staleDatedDaysMaxLength', { maxLength: 3 }),
      },
      min: {
        value: 60,
        message: t('AccountSetup:new_account_form.validation.staleDatedDaysRange', { min: 60, max: 365 }),
      },
      max: {
        value: 365,
        message: t('AccountSetup:new_account_form.validation.staleDatedDaysRange', { min: 60, max: 365 }),
      },
    },
    payeeMatch: {
      maxLength: 3,
    },
    achExceptionProcessingType: {},
    achDecision: {},
    achReason: {},
    createdAt: {},
    isArchiveAccount: {},
  }
}

export const convertTime = ({ timezone, date, format }: { date: ConfigType; timezone?: string; format?: string }) => {
  return dayjs
    .utc(date)
    .tz(timezone)
    .format(format || FORMAT_DATE)
}
